import { CLINICIAN_ROLES, THERAPIST_ROLES } from "../../../assets/appConstants";
import { REGISTRATION_SCENARIOS } from "./registrationWizardConstants";
import phoneService from "../../../services/phoneService";
import {
  hasExplicitLanguage,
  normalizeProfessionTitle,
  resolveLanguage,
} from "./registrationWizardPrefill";

const INVITED_STATUSES = new Set(["INVITED", "invited"]);

export const pickInvitedSubAccount = (dto = {}) => {
  if (dto.subAccountForRegistration) {
    return dto.subAccountForRegistration;
  }
  const subAccounts = dto.subAccounts || [];
  return (
    subAccounts.find((sub) => INVITED_STATUSES.has(sub.status)) ||
    subAccounts[0] ||
    {}
  );
};

/** First explicit language from API candidates (supports he / he-IL, etc.). */
const pickExplicitLanguage = (...candidates) => {
  for (const candidate of candidates) {
    if (hasExplicitLanguage(candidate)) {
      return resolveLanguage(candidate);
    }
  }
  return null;
};

/**
 * Clinic / owner defaults used when the invited clinician has no language of their own.
 */
const pickClinicDefaultLanguage = (clinic = {}, dto = {}) =>
  pickExplicitLanguage(
    clinic.defaultLanguage,
    clinic.languageForPatients,
    clinic.language,
    clinic.ownerLanguage,
    dto.clinic?.defaultLanguage,
    dto.owner?.language,
    dto.owner?.languageForPatients
  );

/**
 * Flatten v2 TherapistForRegistrationDto (`/v2/therapists/account/for-registration`) into wizard-friendly fields.
 */
export const normalizeV2TherapistDto = (dto = {}) => {
  const account = dto.account || {};
  const personal = dto.personal || {};
  const subAccount = pickInvitedSubAccount(dto);
  const clinic = subAccount.clinic || dto.clinic || {};
  const clinicDefaultLanguage = pickClinicDefaultLanguage(clinic, dto);

  return {
    firstName: subAccount.firstName || "",
    lastName: subAccount.lastName || "",
    email: account.email || "",
    phone: account.phone || null,
    clinicName: clinic.name || "",
    role: subAccount.role || "",
    // Own value first; otherwise inherit clinic/owner defaults for invited clinicians. The
    // clinician's personal language is not a source here, so an unset clinic default falls to geo.
    languageForPatients: pickExplicitLanguage(
      subAccount.languageForPatients,
      clinicDefaultLanguage
    ),
    accountStatus: subAccount.status || account.status || "",
    subAccountStatus: subAccount.status || "",
    subAccountId: subAccount.accountId,
    parentAccountId: subAccount.parentAccountId || account.accountId,
    isSubAccount: Boolean(dto.subAccountForRegistration || subAccount.accountId),
    profilePhotoUrl: personal.profilePhoto || null,
    gender: personal.gender || "",
    birthDate: personal.birthDate || "",
    clinicianLanguage: pickExplicitLanguage(personal.language, clinicDefaultLanguage),
    profession: normalizeProfessionTitle(subAccount.title),
    needToSetPassword: dto.needToSetPassword,
    termsAndConditionsAccepted: account.termsAndConditionsAccepted,
    hasNameOnFile: Boolean(
      String(subAccount.firstName || "").trim() && String(subAccount.lastName || "").trim()
    ),
    hasProfilePhotoOnFile: Boolean(personal.profilePhoto || account.profilePhoto),
  };
};

export const pickSubAccountProfileFromTherapistMe = (me = {}) => {
  const flat = me.account || me.personal ? normalizeV2TherapistDto(me) : me;
  return {
    firstName: flat.firstName || me.firstName || me.profile?.firstName || "",
    lastName: flat.lastName || me.lastName || me.profile?.lastName || "",
    languageForPatients: pickExplicitLanguage(
      flat.languageForPatients,
      me.languageForPatients,
      me.clinic?.defaultLanguage
    ),
  };
};

export const pickRoleFromTherapistMe = (me = {}) => {
  const flat = me.account || me.subAccounts ? normalizeV2TherapistDto(me) : me;
  const raw = flat.role || me.role || me.clinicRole || me.permissions?.role || "";
  const normalized = String(raw).toUpperCase();

  if (
    normalized.includes("OWNER") ||
    raw === THERAPIST_ROLES.ROLE_CLINIC_OWNER ||
    raw === THERAPIST_ROLES.ROLE_SUPERUSER
  ) {
    return CLINICIAN_ROLES.OWNER;
  }
  if (normalized.includes("ADMIN") || raw === THERAPIST_ROLES.ROLE_CLINIC_ADMIN) {
    return CLINICIAN_ROLES.ADMIN;
  }
  if (raw === THERAPIST_ROLES.ROLE_CLINIC_BASE || normalized.includes("BASE")) {
    return CLINICIAN_ROLES.BASE;
  }
  return CLINICIAN_ROLES.BASE;
};

export const pickRegistrationScenarioFromTherapistMe = (me = {}, loginAccount = {}) => {
  const explicit =
    loginAccount.registrationScenario || me.registrationScenario || loginAccount.scenario;
  if (
    explicit === REGISTRATION_SCENARIOS.NEW_USER ||
    explicit === REGISTRATION_SCENARIOS.SUB_ACCOUNT
  ) {
    return explicit;
  }

  const flat = me.account || me.subAccountForRegistration || me.subAccounts ? normalizeV2TherapistDto(me) : me;

  if (
    me.subAccountForRegistration ||
    flat.isSubAccount ||
    INVITED_STATUSES.has(flat.subAccountStatus) ||
    INVITED_STATUSES.has(flat.accountStatus) ||
    me.isSubAccount === true ||
    me.subAccountId
  ) {
    return REGISTRATION_SCENARIOS.SUB_ACCOUNT;
  }

  const role = pickRoleFromTherapistMe(me);
  if (flat.clinicName && role !== CLINICIAN_ROLES.OWNER) {
    return REGISTRATION_SCENARIOS.SUB_ACCOUNT;
  }

  return REGISTRATION_SCENARIOS.NEW_USER;
};

export const pickClinicNameFromTherapistMe = (me = {}) => {
  const flat = me.account || me.subAccounts ? normalizeV2TherapistDto(me) : me;
  return flat.clinicName || me.clinicName || me.clinic?.name || "Wizecare";
};

export const pickProfilePhotoFromTherapistMe = (me = {}) => {
  const flat = me.account || me.personal ? normalizeV2TherapistDto(me) : me;
  return flat.profilePhotoUrl || me.profilePhotoUrl || me.profile?.photoUrl || me.photoUrl || null;
};

export const pickPhoneFromTherapistMe = (me = {}) => {
  const flat = me.account || me.personal ? normalizeV2TherapistDto(me) : me;
  const phone = flat.phone || me.phone || me.profile?.phone || null;
  if (!phone) return null;
  return phoneService.normalizeSubAccountPhone(phone);
};

export const pickEmailFromTherapistMe = (me = {}) => {
  const flat = me.account || me.personal ? normalizeV2TherapistDto(me) : me;
  return flat.email || me.email || me.profile?.email || "";
};

export const mapTherapistForRegistrationToWizardAccount = (dto = {}, accountId = "") =>
  mapTherapistMeToWizardAccount(dto, accountId, {
    registrationScenario: REGISTRATION_SCENARIOS.SUB_ACCOUNT,
  });

export const mapTherapistMeToWizardAccount = (me = {}, accountId = "", loginAccount = {}) => {
  const isV2Payload = Boolean(me.account || me.personal || me.subAccounts || me.subAccountForRegistration);
  const flat = isV2Payload ? normalizeV2TherapistDto(me) : me;
  const profile = pickSubAccountProfileFromTherapistMe(isV2Payload ? me : flat);
  const registrationScenario = pickRegistrationScenarioFromTherapistMe(
    isV2Payload ? me : flat,
    loginAccount
  );
  const clinicName =
    registrationScenario === REGISTRATION_SCENARIOS.NEW_USER
      ? pickClinicNameFromTherapistMe(isV2Payload ? me : flat) === "Wizecare"
        ? null
        : pickClinicNameFromTherapistMe(isV2Payload ? me : flat)
      : pickClinicNameFromTherapistMe(isV2Payload ? me : flat);

  return {
    accountId: accountId || flat.parentAccountId || me.account?.accountId,
    subAccountId: flat.subAccountId,
    ...profile,
    clinicName,
    role: pickRoleFromTherapistMe(isV2Payload ? me : flat),
    registrationScenario,
    phone: pickPhoneFromTherapistMe(isV2Payload ? me : flat),
    email: pickEmailFromTherapistMe(isV2Payload ? me : flat),
    profilePhotoUrl: pickProfilePhotoFromTherapistMe(isV2Payload ? me : flat),
    clinicianLanguage: pickExplicitLanguage(
      flat.clinicianLanguage,
      profile.languageForPatients
    ),
    gender: flat.gender,
    birthDate: flat.birthDate,
    profession: flat.profession,
    needToSetPassword: flat.needToSetPassword,
    termsAndConditionsAccepted: flat.termsAndConditionsAccepted,
    hasNameOnFile: flat.hasNameOnFile,
    hasProfilePhotoOnFile: flat.hasProfilePhotoOnFile,
  };
};
